import { useState, useCallback, useMemo } from 'react';

export function useTableRowSelection<T extends { id: string }>(items: T[]) {
  const keys = useMemo(() => items.map((item) => item.id), [items]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // Select a row
  const selectRow = useCallback((id: string) => {
    setSelectedRows((prev) => [...prev, id]);
  }, []);

  // Unselect a row
  const unselectRow = useCallback((id: string) => {
    setSelectedRows((prev) => prev.filter((k) => k !== id));
  }, []);

  // Select all rows
  const selectAll = useCallback(() => {
    setSelectedRows(keys);
  }, [keys]);

  // Unselect all rows
  const unSelectAll = useCallback(() => {
    setSelectedRows([]);
  }, []);

  // Check if all rows are selected
  const isAllSelected = useMemo(() => {
    return (
      selectedRows.length !== 0 &&
      keys.length > 0 &&
      selectedRows.length === keys.length
    );
  }, [selectedRows, keys]);

  // Check if a row is selected
  const isSelected = useCallback(
    (key: string) => {
      return selectedRows.includes(key);
    },
    [selectedRows],
  );

  // Toggle select all rows
  const onToggleSelectAll = useCallback(
    (isSelectedAll: boolean) => {
      if (isSelectedAll) {
        selectAll();
      } else {
        unSelectAll();
      }
    },
    [selectAll, unSelectAll],
  );

  // Toggle select a row
  const onToggleSelect = useCallback(
    (id: string, selected: boolean) => {
      if (selected) {
        selectRow(id);
      } else {
        unselectRow(id);
      }
    },
    [selectRow, unselectRow],
  );

  return {
    selectedRows,
    isAllSelected,
    isSelected,
    onToggleSelectAll,
    onToggleSelect,
  };
}
